import type {
  OpenAccountCancelParams,
  OpenAccountConfirmAccountTypeParams,
  OpenAccountConfirmAccountTypePreview,
  OpenAccountConfirmAccountTypePreviewParams,
  OpenAccountConfirmAccountTypeResponse,
  OpenAccountCreateParams,
  OpenAccountDetail,
  OpenAccountListItem,
  OpenAccountListParams,
  OpenAccountQuotePreview,
  OpenAccountQuotePreviewParams,
  OpenAccountRegisterResultParams,
  OpenAccountSelectAgencyParams,
  OpenAccountSelectAgencyPreview,
  OpenAccountSelectAgencyPreviewParams,
  OpenAccountSelectAgencyResponse,
  OpenAccountTimeZoneItem,
  OpenAccountTimeZoneOption,
} from './types'

import type { PagingRequest } from '#/api/type'

import { isNil } from 'lodash-es'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { formatDateRange } from '#/utils'

export interface OpenAccountListResponse {
  list?: OpenAccountListItem[]
  total?: number
}

export interface OpenAccountTimeZoneResponse {
  list?: OpenAccountTimeZoneItem[]
}

export const openAccountApi = defineApi('operation', ({ prefix }) => {
  /**
   * 查询开户管理列表。
   * POST /openAdsAccount/query
   */
  function getList(params: PagingRequest<OpenAccountListParams>) {
    const condition = params.condition
    return requestClient
      .post<OpenAccountListResponse>(prefix('openAdsAccount/query'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        condition: {
          timeRange: formatDateRange(condition?.timeRange),
          mediaPlatform: condition?.mediaPlatform,
          userId: condition?.userId,
          customerId: condition?.customerId,
          customerName: condition?.customerName,
          status: isNil(condition?.status)
            ? undefined
            : Number(condition?.status),
          paymentStatus: isNil(condition?.paymentStatus)
            ? undefined
            : Number(condition?.paymentStatus),
          categoryCode: condition?.categoryCode,
          accountCurrency: condition?.accountCurrency,
          applyId: condition?.applyId,
        },
      })
      .then((res) => ({
        items: res?.list ?? [],
        total: res?.total,
      }))
  }

  /**
   * 预览运营代提交开户报价。
   * POST /openAdsAccount/quote/preview
   */
  function previewQuote(params: OpenAccountQuotePreviewParams) {
    return requestClient.post<OpenAccountQuotePreview>(
      prefix('openAdsAccount/quote/preview'),
      params,
    )
  }

  /**
   * 运营代客户提交开户申请。
   * POST /openAdsAccount/create
   */
  function create(params: OpenAccountCreateParams) {
    return requestClient.post(prefix('openAdsAccount/create'), params)
  }

  /**
   * 查询开户申请详情。
   * POST /openAdsAccount/detail
   */
  function getDetail(applyId: string) {
    return requestClient
      .post<OpenAccountDetail>(prefix('openAdsAccount/detail'), { applyId })
      .then((res) => ({
        accountCurrency: res.accountCurrency,
        accountNum: res.accountNum,
        accountType: res.accountType === '' ? undefined : res.accountType,
        agency: res.agency === '' ? undefined : res.agency,
        applyId: res.applyId,
        businessUrl: res.businessUrl,
        categoryCode: res.categoryCode,
        clientStatus: res.clientStatus,
        countryCode: res.countryCode,
        countryName: res.countryName,
        createdTime: res.createdTime,
        customerId: res.customerId,
        customerName: res.customerName,
        dailyBudget: res.dailyBudget,
        finalDisplayTotal: res.finalDisplayTotal,
        firstRechargePerAccount: res.firstRechargePerAccount,
        firstRechargeTotal: res.firstRechargeTotal,
        initialDisplayTotal: res.initialDisplayTotal,
        isFeeModified: res.isFeeModified,
        mediaPlatform: res.mediaPlatform,
        openingFeeCurrency: res.openingFeeCurrency,
        openingFeeOrderNo: res.openingFeeOrderNo,
        paymentStatus: res.paymentStatus,
        quoteVersion: res.quoteVersion,
        rechargeOrderNos: res.rechargeOrderNos,
        remark: res.remark,
        resultAccounts: res.resultAccounts,
        status: res.status,
        timeZone: res.timeZone,
        userId: res.userId,
        autoDeductAuthorized: res.autoDeductAuthorized,
        assetIds: res.assetIds,
        businessUrls: res.businessUrls,
        defaultWalletBalance: res.defaultWalletBalance,
        handleType: res.handleType,
        items: res.items,
        openingFeeUsd: res.openingFeeUsd,
        openingFeePerAccount: res.openingFeePerAccount,
        pages: res.pages,
        payments: res.payments,
        walletCurrency: res.walletCurrency,
      }))
  }

  /**
   * 预览确认账户类型和金额。
   * POST /openAdsAccount/confirmAccountType/preview
   */
  function previewConfirmAccountType(
    params: OpenAccountConfirmAccountTypePreviewParams,
  ) {
    return requestClient.post<OpenAccountConfirmAccountTypePreview>(
      prefix('openAdsAccount/confirmAccountType/preview'),
      params,
    )
  }

  /**
   * 确认账户类型和金额。
   * POST /openAdsAccount/confirmAccountType
   */
  function confirmAccountType(params: OpenAccountConfirmAccountTypeParams) {
    return requestClient.post<OpenAccountConfirmAccountTypeResponse>(
      prefix('openAdsAccount/confirmAccountType'),
      params,
    )
  }

  /**
   * 预览选择开户代理后的金额与处理方式。
   * POST /openAdsAccount/selectAgency/preview
   */
  function previewSelectAgency(params: OpenAccountSelectAgencyPreviewParams) {
    return requestClient.post<OpenAccountSelectAgencyPreview>(
      prefix('openAdsAccount/selectAgency/preview'),
      params,
    )
  }

  /**
   * 确认或退回开户代理选择。
   * POST /openAdsAccount/selectAgency
   */
  function selectAgency(params: OpenAccountSelectAgencyParams) {
    return requestClient.post<OpenAccountSelectAgencyResponse>(
      prefix('openAdsAccount/selectAgency'),
      params,
    )
  }

  /**
   * 取消开户申请。
   * POST /openAdsAccount/cancel
   */
  function cancel(params: OpenAccountCancelParams) {
    return requestClient.post(prefix('openAdsAccount/cancel'), params)
  }

  /**
   * 重新打开开户审核。
   * POST /openAdsAccount/reopen
   */
  function reopen(applyId: string) {
    return requestClient.post(prefix('openAdsAccount/reopen'), { applyId })
  }

  /**
   * 重试开户扣款异常。
   * POST /openAdsAccount/retryPay
   */
  function retryPay(applyId: string) {
    return requestClient.post(prefix('openAdsAccount/retryPay'), { applyId })
  }

  /**
   * 登记开户结果。
   * POST /openAdsAccount/registerResult
   */
  function registerResult(params: OpenAccountRegisterResultParams) {
    return requestClient.post(prefix('openAdsAccount/registerResult'), params)
  }

  /**
   * 查询开户时区选项。
   * POST /openAdsAccount/timeZone
   */
  function getTimeZones() {
    return requestClient
      .post<OpenAccountTimeZoneResponse>(prefix('openAdsAccount/timeZone'), {})
      .then((res): OpenAccountTimeZoneOption[] =>
        (res?.list ?? []).map((item) => ({
          label: item.region
            ? `${item.region} / ${item.timeZone}`
            : String(item.timeZone),
          value: String(item.timeZone),
        })),
      )
  }

  return {
    cancel,
    confirmAccountType,
    create,
    getDetail,
    getList,
    getTimeZones,
    previewConfirmAccountType,
    previewQuote,
    previewSelectAgency,
    registerResult,
    reopen,
    retryPay,
    selectAgency,
  }
})
